// shared/logo.ts: optical logo sizing (design system A, docs/design/system/tokens-a.md §1). Every place a builder's
// logo sits is a box, a max height and a max width. Inside it a logo gets the area of a square 1.2 times the box's
// height (LOGO_OPTICAL), so a wide wordmark comes down to about the weight of a compact badge, and a badge (a 5:4
// lockup, say) keeps the box's full height. The app does this in CSS (the --logo-* tokens in
// src/styles/tokens.css and .hb-logo in direction.css); the emails and the offline page use logoSize().
// tests/platform/logo-tokens.node.test.ts keeps the two in step.

export type LogoBox = { h: number; w: number };

/**
 * The side of the square a logo's area matches, as a share of the box's height. Anything from about 1.12 (a 5:4
 * badge's area) keeps a badge at the box's full height; 1.2 gives a wordmark a touch more presence, since a line of
 * letters reads lighter than a badge of the same area.
 */
export const LOGO_OPTICAL = 1.2;

/** The boxes, in CSS pixels. tokens.css declares the same values as --logo-<slot>-h and --logo-<slot>-w. */
export const LOGO_BOXES = {
  /** The desktop sidebar, the footer, the sign-in panel and the small pages. */
  side: { h: 44, w: 136 },
  /** The phone header (the board's, the team's, Messages'), and Start Your Build and sign-in on a phone. */
  bar: { h: 40, w: 124 },
  /** Start Your Build's header and the demo key on desktop, and the offline page. */
  door: { h: 52, w: 160 },
  /** Emails. */
  mail: { h: 44, w: 136 },
} as const satisfies Record<string, LogoBox>;

export type LogoSlot = keyof typeof LOGO_BOXES;

/**
 * A stacked logo (a mark with its name and a line of words under it, aspect about 1.4 to 2.4) or a tall one (aspect under 0.9)
 * gets a slot this much taller than a wide wordmark or a compact badge does, so its name can be read. A square or 5:4 badge
 * (aspect up to about 1.3) and a wide wordmark (aspect over 2.4) keep their slots as they were.
 */
export const STACKED_BOOST = 1.5;
export const STACKED_MIN_AR = 1.4;
export const STACKED_MAX_AR = 2.4;
export const TALL_MAX_AR = 0.9;

/** True for a stacked or tall logo (see STACKED_BOOST). */
export function isStacked(aspect: number): boolean {
  return Number.isFinite(aspect) && aspect > 0 && (aspect < TALL_MAX_AR || (aspect >= STACKED_MIN_AR && aspect < STACKED_MAX_AR));
}

/** The plain badge every new builder starts with (public/brand/starter.png), until it uploads its own logo. */
export const STARTER_LOGO = "/brand/starter.png";

/** True for the starter badge: surfaces show the company name as text in its place (a blank ink square reads as broken). */
export function isStarterLogo(logo: { onLight: string }): boolean {
  return logo.onLight === STARTER_LOGO;
}

/** Whether a logo (by its pixel size) is stacked or tall, for the setup screens that show it at a size of their own. */
export function logoIsStacked(logo: { width?: number; height?: number }): boolean {
  return isStacked(logoAspect(logo));
}

/** A logo's width over its height, from its pixel size; 1 when the size is missing or broken. */
export function logoAspect(logo: { width?: number; height?: number }): number {
  const { width, height } = logo;
  return typeof width === "number" && typeof height === "number" && width > 0 && height > 0 ? width / height : 1;
}

/**
 * The size a logo of this aspect takes in a box: no wider and no taller than the box, and no larger in area than a
 * square LOGO_OPTICAL times the box's height. A stacked or tall logo's box is STACKED_BOOST times as tall. Whole pixels.
 */
export function logoSize(aspect: number, box: LogoBox): { width: number; height: number } {
  const ar = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
  const h = isStacked(ar) ? box.h * STACKED_BOOST : box.h;
  const width = Math.min(box.w, h * ar, h * LOGO_OPTICAL * Math.sqrt(ar));
  return { width: Math.round(width), height: Math.round(width / ar) };
}
